import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { MapPin, Store, Phone } from 'lucide-react';
import useSEO, { SEO_CONFIG } from '@/hooks/useSEO';

const STORES = [
  { name: 'Cave du Mistral', address: '3 Avenue de la 1ère DFL, 13210 Saint-Rémy-de-Provence', city: 'Saint-Rémy-de-Provence' },
  { name: 'Cave et Domaine', address: '1 Avenue du 19 Mars, 13210 Saint-Rémy-de-Provence', city: 'Saint-Rémy-de-Provence' },
  { name: "Chez Karen — L'Épicurieuse", address: '48 Avenue Durand Maillane, 13210 Saint-Rémy-de-Provence', city: 'Saint-Rémy-de-Provence' },
  { name: 'Comptoir des Vignes', address: '820 Boulevard Ernest Genevet, 13160 Châteaurenard', city: 'Châteaurenard' },
  { name: 'Cave des Passionnés', address: "45 Boulevard de l'Ancien Marché, 13870 Rognonas", city: 'Rognonas' },
  { name: 'Cave à Vin', address: '4 Avenue des Arches, 13200 Arles', city: 'Arles' },
  { name: 'V and B Avignon Mistral 7', address: '180 Rue Éric Rohmer, 84140 Avignon', city: 'Avignon' },
  { name: 'Choppe et Compagnie', address: "200 Route d'Avignon, 84100 Orange", city: 'Orange' },
  { name: 'Caves Tanins', address: '2 Route de Saint-Rémy, 13520 Maussane-les-Alpilles', city: 'Maussane-les-Alpilles' },
];

const mapsUrl = (q) => `https://maps.google.com/?q=${encodeURIComponent(q)}`;

export default function PointsDeVente() {
  useSEO(SEO_CONFIG.pointsDeVente);

  return (
    <div className="py-24 lg:py-32">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-12">
          <h1 className="text-5xl lg:text-6xl font-heading text-foreground mb-4">Où acheter nos bières artisanales en Provence ?</h1>
          <p className="text-lg text-foreground/70">
            Retrouvez les bières artisanales Soif de Provence chez nos revendeurs partenaires
            dans les Alpilles et les Bouches-du-Rhône, lors de nos événements, ou en vente directe à la brasserie.
          </p>
        </div>

        {/* Vente directe brasserie */}
        <div className="bg-primary text-primary-foreground rounded-2xl p-8 mb-16 grid md:grid-cols-[1fr_auto] gap-6 items-center" data-testid="direct-sale-card">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Store className="w-6 h-6" />
              <h2 className="text-2xl font-heading">Vente directe à la brasserie</h2>
            </div>
            <p className="text-primary-foreground/90 mb-1">1688 petite route des Jardins, 13210 Saint-Rémy-de-Provence</p>
            <p className="text-primary-foreground/80 text-sm">Contactez-nous pour connaître les horaires et la disponibilité.</p>
          </div>
          <div className="flex flex-col sm:flex-row md:flex-col gap-3">
            <a href={mapsUrl('1688 petite route des Jardins, 13210 Saint-Rémy-de-Provence')} target="_blank" rel="noopener noreferrer">
              <Button variant="secondary" className="w-full rounded-full" data-testid="brewery-maps-button">
                <MapPin className="w-4 h-4 mr-2" />
                Voir sur la carte
              </Button>
            </a>
            <a href="tel:+33664822759">
              <Button variant="secondary" className="w-full rounded-full">
                <Phone className="w-4 h-4 mr-2" />
                06 64 82 27 59
              </Button>
            </a>
          </div>
        </div>

        <h2 className="text-3xl font-heading text-foreground mb-8">Nos revendeurs partenaires</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
          {STORES.map((store) => (
            <div
              key={store.name}
              className="bg-white border border-stone-200 rounded-2xl p-6 hover:border-primary/30 hover:shadow-xl transition-all flex flex-col"
              data-testid={`store-${store.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            >
              <span className="inline-block text-xs font-medium text-accent uppercase tracking-wide mb-2">{store.city}</span>
              <h3 className="text-xl font-heading text-foreground mb-2">{store.name}</h3>
              <p className="text-foreground/70 text-sm flex-1">{store.address}</p>
              <a href={mapsUrl(`${store.name} ${store.address}`)} target="_blank" rel="noopener noreferrer" className="mt-4">
                <Button variant="outline" className="w-full rounded-full border-2">
                  <MapPin className="w-4 h-4 mr-2" />
                  Itinéraire
                </Button>
              </a>
            </div>
          ))}
        </div>

        <div className="bg-secondary border border-border rounded-2xl p-10 text-center">
          <h2 className="text-2xl font-heading text-foreground mb-3">Vous ne trouvez pas de point de vente près de chez vous ?</h2>
          <p className="text-foreground/70 mb-6">Contactez-nous, nous vous orienterons vers la solution la plus proche.</p>
          <Link to="/contact">
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6" data-testid="pdv-contact-button">
              Nous contacter
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
